"use client";

import { Howl } from "howler";
import {
  createContext,
  ReactEventHandler,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUserMedia } from "@/app/(v1)/hooks/user-media/use-user-media";
import { cn } from "@/lib/utils";
import { useDebounceCallback } from "@/lib/hooks";
import {
  ArrowLeft,
  Loader2,
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  VolumeUp,
} from "@/components/ui/icons";
import { motion } from "framer-motion";
import { Slot } from "@radix-ui/react-slot";
import {
  UserMediaMonitorProvider,
  useUserMediaStream,
} from "../hooks/user-media/media-monitor";
import { UserMediaMonitor } from "../hooks/user-media/lib/track-monitor";
import {
  useDeviceFilters,
  useDevices,
  type DeviceFilterKind,
} from "../hooks/user-media/use-devices";
import type { InitializationState } from "../hooks/user-media/types";

type VideoStatus = "idle" | "loading" | "ready";

type UserMediaUIContextValue = {
  stream: MediaStream | null;
  status: string;
  error: string | null;
  videoStatus: VideoStatus;
  videoHealth: string;
  videoError: string | null;
  initialize: (type?: "audio" | "video" | "audio-video") => void;
  selectAudioSource: (deviceId: string) => void;
  selectVideoSource: (deviceId: string) => void;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  audioDeviceId: string | null;
  videoDeviceId: string | null;
  audioDevices: MediaDeviceInfo[];
  videoDevices: MediaDeviceInfo[];
  audioOutputDevices: MediaDeviceInfo[];
  monitor: UserMediaMonitor | null;
  isAudioActive: boolean;
  isVideoActive: boolean;
  toggleAudio: () => void;
  toggleVideo: () => void;
  fetchDevices: () => Promise<void>;
  setVideoElement: (el: HTMLVideoElement | null) => void;
  handleVideoLoadedData: () => void;
  isStreamActive: boolean;
  initializationState: InitializationState;
};

const UserMediaUIContext = createContext<UserMediaUIContextValue | null>(null);

export const useUserMediaContext = () => {
  const context = useContext(UserMediaUIContext);

  if (!context) {
    throw new Error("useUserMediaContext must be used within <UserMedia>");
  }

  return context;
};

const UserMediaUIProvider = ({ children }: { children: ReactNode }) => {
  const {
    stream,
    status,
    error,
    initialize,
    isAudioActive,
    isVideoActive,
    selectAudioSource,
    selectVideoSource,
    toggleAudio,
    toggleVideo,
    devices,
    getDevices,
    monitor,
    initializationState,
  } = useUserMedia();
  const { devices: deviceLists } = useDevices();

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoStatus, setVideoStatus] = useState<VideoStatus>("idle");
  const audioDevices = deviceLists.microphone;
  const videoDevices = deviceLists.video;
  const audioOutputDevices = deviceLists.speaker;
  const resolveSelectedDeviceId = useCallback(
    (deviceId: string | null, deviceList: MediaDeviceInfo[]) => {
      if (!deviceId) return null;
      return deviceList.some((device) => device.deviceId === deviceId)
        ? deviceId
        : null;
    },
    [],
  );
  const resolvedAudioDeviceId = resolveSelectedDeviceId(
    devices.audio.activeDeviceId || null,
    audioDevices,
  );
  const resolvedVideoDeviceId = resolveSelectedDeviceId(
    devices.video.activeDeviceId || null,
    videoDevices,
  );

  const handleVideoLoadedData = () => {
    setVideoStatus("ready");
  };

  const fetchDevices = useCallback(async () => {
    await getDevices();
  }, [getDevices]);

  const setVideoElement = useCallback(
    (node: HTMLVideoElement | null) => {
      videoRef.current = node;

      if (!node) return;

      if (stream) {
        node.srcObject = stream;
        const ready =
          node.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA
            ? "ready"
            : "loading";
        setVideoStatus(ready);
      } else {
        node.srcObject = null;
        setVideoStatus("idle");
      }
    },
    [stream],
  );

  const contextValue = {
    stream,
    status,
    error,
    videoStatus,
    videoHealth: devices.video.health.status || "idle",
    videoError: devices.video.health.error || null,
    initialize,
    audioDevices,
    videoDevices,
    audioOutputDevices,
    isAudioActive,
    isVideoActive,
    selectAudioSource,
    selectVideoSource,
    toggleAudio,
    toggleVideo,
    fetchDevices,
    videoRef,
    handleVideoLoadedData,
    videoDeviceId: resolvedVideoDeviceId,
    audioDeviceId: resolvedAudioDeviceId,
    monitor,
    setVideoElement,
    isStreamActive: Boolean(stream?.active),
    initializationState,
  };

  return (
    <UserMediaUIContext.Provider value={contextValue}>
      {children}
    </UserMediaUIContext.Provider>
  );
};

type UserMediaRootProps = {
  children: ReactNode;
};

const UserMediaRoot = ({ children }: UserMediaRootProps) => {
  return (
    <UserMediaMonitorProvider>
      <UserMediaUIProvider>
        <Slot className="@container">{children}</Slot>
      </UserMediaUIProvider>
    </UserMediaMonitorProvider>
  );
};

type UserMediaPlayerProps = {
  children?: ReactNode;
  style?: React.CSSProperties;
  className?: string;
  videoClassName?: string;
  inactiveContent?: ReactNode;
  onLoadedData?: ReactEventHandler<HTMLVideoElement>;
};

const UserMediaPlayer = ({
  children,
  className,
  style,
  videoClassName,
  onLoadedData,
}: UserMediaPlayerProps) => {
  const { handleVideoLoadedData, setVideoElement, videoStatus, stream } =
    useUserMediaContext();
  const isActive = Boolean(stream?.active);
  const streamId = stream?.id ?? "idle";
  const handleVideoLoaded: ReactEventHandler<HTMLVideoElement> = useCallback(
    (e) => {
      handleVideoLoadedData();
      onLoadedData?.(e);
    },
    [],
  );
  const throttledHandleVideoLoaded = useDebounceCallback(
    handleVideoLoaded,
    500,
  );

  return (
    <div
      className={cn(
        "relative flex h-[300px] w-full overflow-hidden rounded-md border bg-muted/40",
        className,
      )}
    >
      <video
        key={streamId}
        ref={setVideoElement}
        autoPlay
        muted
        playsInline
        onLoadedData={throttledHandleVideoLoaded}
        className={cn(
          "h-full w-full object-cover transition-opacity duration-300",
          videoStatus === "ready" ? "opacity-100" : "opacity-0",
          videoClassName,
        )}
        style={style}
      />

      {isActive && videoStatus === "loading" && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/60 text-white">
          <Loader2 className="size-6 animate-spin" />
        </div>
      )}

      {children}
    </div>
  );
};

type UserMediaOverlayProps = {
  children?: ReactNode;
  className?: string;
  disableGradient?: boolean;
  interactive?: boolean;
};

const UserMediaOverlay = ({
  children,
  className,
  disableGradient = false,
  interactive = false,
}: UserMediaOverlayProps) => {
  const userStream = useUserMediaStream();
  const isStreamActive = userStream?.active;
  if (!isStreamActive) return null;

  return (
    <div
      className={cn(
        "absolute inset-0 z-30",
        interactive ? "pointer-events-auto" : "pointer-events-none",
        className,
      )}
    >
      {!disableGradient && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 w-[calc(100%+32px)] -translate-x-[16px] bg-gradient-to-t from-black from-65% to-transparent blur-md translate-y-1/2" />
      )}
      <div className="relative h-full w-full">{children}</div>
    </div>
  );
};

type UserMediaOverlayFooterProps = {
  children?: ReactNode;
  className?: string;
};

const UserMediaOverlayFooter = ({
  children,
  className,
}: UserMediaOverlayFooterProps) => {
  return (
    <div
      className={cn(
        "absolute inset-x-0 bottom-0 z-40 flex items-center justify-between px-3 pb-3 text-xs text-white",
        className,
      )}
    >
      {children}
    </div>
  );
};

type UserMediaControlsProps = {
  children?: ReactNode;
  className?: string;
};

const UserMediaControls = ({ children, className }: UserMediaControlsProps) => {
  return (
    <div
      className={cn(
        "relative flex flex-col pt-2 gap-2 -ml-1 @lg:flex-row @lg:flex-wrap",
        className,
      )}
    >
      {children}
    </div>
  );
};

const UserMediaDeviceDebugPanel = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { rawDevices } = useDevices();
  const { filters, setAll, setNone, toggleDevice } = useDeviceFilters();

  const getDeviceLabel = useCallback(
    (device: MediaDeviceInfo, fallbackLabel: string) => {
      if (device.deviceId === "default") return "System default";
      if (device.deviceId === "communications") return "Communications";
      if (device.label) return device.label;
      if (device.deviceId) return `${fallbackLabel} (${device.deviceId})`;
      return `${fallbackLabel} (unknown)`;
    },
    [],
  );

  const renderDeviceSection = (
    kind: DeviceFilterKind,
    title: string,
    placeholder: string,
  ) => {
    const deviceList = rawDevices[kind];
    const filter = filters[kind];
    const deviceIds = deviceList.map((device) => device.deviceId);
    const visibleIds =
      filter.mode === "all"
        ? deviceIds
        : filter.visibleIds.filter((id) => deviceIds.includes(id));

    return (
      <div className="flex flex-col gap-1.5 rounded-md border border-border/60 bg-background/80 p-2">
        <div className="flex items-center justify-between">
          <span className="text-[9px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
            {title}
          </span>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="outline"
              className="h-6 px-1.5 text-[9px]"
              onClick={() => setAll(kind, deviceIds)}
            >
              Show all
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-6 px-1.5 text-[9px]"
              onClick={() => setNone(kind)}
            >
              None
            </Button>
          </div>
        </div>
        {deviceList.length === 0 ? (
          <p className="text-[11px] text-muted-foreground/70">
            No devices detected.
          </p>
        ) : (
          <div className="flex flex-col gap-1 max-h-28 overflow-y-auto pr-1">
            {deviceList.map((device, index) => {
              const checkboxId = `dev-device-${kind}-${device.deviceId || index}`;
              return (
                <div
                  key={checkboxId}
                  className="flex items-center gap-1.5 text-[11px] text-muted-foreground"
                >
                  <Checkbox
                    id={checkboxId}
                    checked={visibleIds.includes(device.deviceId)}
                    onCheckedChange={() =>
                      toggleDevice(kind, device.deviceId, deviceIds)
                    }
                  />
                  <Label htmlFor={checkboxId} className="cursor-pointer">
                    {getDeviceLabel(device, placeholder)}
                  </Label>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed right-4 top-4 z-[100] w-[260px] rounded-md border border-dashed border-border/70 bg-muted/80 p-2 shadow-xl backdrop-blur">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[9px] font-semibold uppercase tracking-[0.3em] text-muted-foreground">
            Device Debug
          </span>
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-700">
            Dev
          </span>
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? (
            <svg
              className="h-3 w-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          ) : (
            <svg
              className="h-3 w-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 15l7-7 7 7"
              />
            </svg>
          )}
        </Button>
      </div>
      {isExpanded && (
        <div className="mt-2 grid gap-2">
          {renderDeviceSection("microphone", "Microphones", "Microphone")}
          {renderDeviceSection("video", "Cameras", "Camera")}
          {renderDeviceSection("speaker", "Speakers", "Speaker")}
        </div>
      )}
    </div>
  );
};

type DeviceSelectProps = {
  devices: MediaDeviceInfo[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder: string;
  className?: string;
  includeDefaultOption?: boolean;
};

const DeviceSelect = ({
  devices,
  value,
  onChange,
  placeholder,
  className,
  includeDefaultOption = false,
}: DeviceSelectProps) => {
  const { initialize } = useUserMediaContext();
  const normalizedValue =
    includeDefaultOption && value === null ? "default" : (value ?? undefined);
  const noDevices =
    devices.length === 0 || devices.every((device) => device.deviceId === "");

  return (
    <Select value={normalizedValue} onValueChange={onChange}>
      <SelectTrigger
        className={cn(
          "h-fit max-w-[120px] border-none px-0 text-sm hover:bg-muted [&>span]:max-w-[calc(100%-theme(spacing.5))] [&>span]:truncate focus:ring-0",
          className,
        )}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent alignOffset={-32} className={cn("w-[calc(100%+32px)]")}>
        {includeDefaultOption && (
          <SelectItem className="text-sm" value="default">
            System default
          </SelectItem>
        )}
        {noDevices ? (
          <SelectGroup>
            <SelectLabel
              key="null"
              className="max-w-[220px] mx-auto text-sm text-center items-center flex flex-col gap-4"
            >
              <p>
                No devices available. Please allow permissions or check your
                devices are properly connected
              </p>
              <Button
                size={"sm"}
                className="bg-white text-black hover:bg-gray-100 rounded-full h-8"
                onClick={() => initialize()}
              >
                Allow Devices
              </Button>
            </SelectLabel>
          </SelectGroup>
        ) : (
          devices.map((device) => {
            const label =
              device.deviceId === "default"
                ? "System default"
                : device.deviceId === "communications"
                  ? "Communications"
                  : device.label || placeholder;
            return (
              <SelectItem
                className="text-xs"
                key={device.deviceId}
                value={device.deviceId}
              >
                {label}
              </SelectItem>
            );
          })
        )}
      </SelectContent>
    </Select>
  );
};

const ControlFieldset = ({
  children,
  className,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <fieldset
    className={cn(
      "flex h-fit items-center gap-2 rounded-sm p-1 hover:bg-muted ring-ring focus-within:ring-1 focus-within:ring-offset-1",
      className,
    )}
  >
    {children}
  </fieldset>
);

type UserMediaAudioControlProps = {
  onDeviceChange?: (deviceId: string) => void;
};

const UserMediaAudioControl = ({
  onDeviceChange,
}: UserMediaAudioControlProps) => {
  const {
    status,
    error,
    isAudioActive,
    toggleAudio,
    audioDevices,
    audioDeviceId,
    selectAudioSource,
  } = useUserMediaContext();

  const handleDeviceChange = useCallback(
    (deviceId: string) => {
      selectAudioSource(deviceId);
      onDeviceChange?.(deviceId);
    },
    [selectAudioSource, onDeviceChange],
  );

  return (
    <ControlFieldset>
      <Button
        variant={isAudioActive ? "outline" : "secondary"}
        size="icon"
        onClick={toggleAudio}
        className={cn("size-7", !isAudioActive && "opacity-80")}
      >
        {isAudioActive ? (
          <Mic className="size-4" />
        ) : (
          <MicOff className="size-4" />
        )}
      </Button>
      <DeviceSelect
        devices={audioDevices}
        value={audioDeviceId}
        onChange={handleDeviceChange}
        placeholder="Select microphone"
      />
      {(status === "warning" || status === "error") && (
        <span
          className={cn(
            "text-xs max-w-[140px] truncate",
            status === "error" ? "text-red-400" : "text-amber-300",
          )}
          title={error ?? "Microphone issue"}
        >
          {error ?? "Microphone issue"}
        </span>
      )}
    </ControlFieldset>
  );
};

const UserMediaVideoControl = () => {
  const {
    isVideoActive,
    toggleVideo,
    videoDevices,
    videoDeviceId,
    selectVideoSource,
  } = useUserMediaContext();

  const handleDeviceChange = useCallback(
    (deviceId: string) => {
      selectVideoSource(deviceId);
    },
    [selectVideoSource],
  );

  return (
    <ControlFieldset>
      <Button
        variant={isVideoActive ? "outline" : "secondary"}
        size="icon"
        onClick={toggleVideo}
        className={cn("size-7", !isVideoActive && "opacity-80")}
      >
        {isVideoActive ? (
          <Videocam className="size-4" />
        ) : (
          <VideocamOff className="size-4" />
        )}
      </Button>
      <DeviceSelect
        devices={videoDevices}
        value={videoDeviceId}
        onChange={handleDeviceChange}
        placeholder="Select camera"
      />
    </ControlFieldset>
  );
};

const UserMediaSpeakerControl = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [bells, setBells] = useState<Array<{ id: string; progress: number }>>(
    [],
  );
  const bellQueueRef = useRef<string[]>([]);
  const bellIdCounterRef = useRef(0);

  const addBell = useCallback(() => {
    const bellId = `bell-${bellIdCounterRef.current++}`;
    bellQueueRef.current.push(bellId);

    setBells((prev) => [...prev, { id: bellId, progress: 0 }]);

    // Create animation
    let startTime: number | null = null;
    const animateBell = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const duration = 800; // 800ms animation
      const progress = Math.min(elapsed / duration, 1);

      setBells((prev) =>
        prev.map((bell) => (bell.id === bellId ? { ...bell, progress } : bell)),
      );

      if (progress < 1) {
        requestAnimationFrame(animateBell);
      } else {
        // Remove bell from queue and state when animation completes
        bellQueueRef.current = bellQueueRef.current.filter(
          (id) => id !== bellId,
        );
        setBells((prev) => prev.filter((bell) => bell.id !== bellId));
      }
    };

    requestAnimationFrame(animateBell);

    // Play sound
    const sound = new Howl({
      src: ["../ding.mp3"],
    });
    sound.play();
  }, []);

  const calculateBellPosition = (progress: number) => {
    // Move right (x-axis translation) - extend to 280px to finish at end of container
    const x = progress * 280;

    // Oscillate (y-axis) - more chaotic motion with multiple frequencies
    const randomAmplitude = Math.random() * 4.5 + 0.75; // Random factor between 0.75 and 1.25
    const randomInitialOrigin = Math.random() * 2 - 1; // Random value between -1 and 1
    const oscillation =
      Math.sin(progress * Math.PI * 4) * 20 * randomAmplitude + // Primary oscillation with more amplitude
      Math.sin(progress * Math.PI * 7) * 12 * randomAmplitude + // Secondary chaos frequency
      Math.cos(progress * Math.PI * 2.3) * 8 * randomAmplitude + // Tertiary chaos frequency
      randomInitialOrigin * 30 * (1 - progress); // Random initial origin that fades out

    return {
      x,
      y: oscillation,
      scale: 1 + progress * 2, // Scale from 1 to 1.2
    };
  };

  return (
    <motion.div
      className="group will-change-contents relative overflow-visible"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => addBell()}
    >
      {/* Bell animations container */}
      <div className="absolute inset-0 pointer-events-none overflow-visible">
        {bells.map((bell) => {
          const { x, y, scale } = calculateBellPosition(bell.progress);
          return (
            <motion.div
              key={bell.id}
              className="absolute left-5 top-0 z-20"
              initial={{ x: 0, y: 0, scale: 1, opacity: 1 }}
              animate={{
                x: `${x}px`,
                y: `${y}px`,
                scale,
                opacity:
                  bell.progress > 0.8 ? 1 - (bell.progress - 0.2) * 5 : 1,
              }}
              transition={{ type: "tween", duration: 0.8, ease: "easeOut" }}
            >
              🛎️
            </motion.div>
          );
        })}
      </div>

      <ControlFieldset className="hover:bg-transparent rounded-md p-1 group-hover:bg-gradient-to-r from-muted to-transparent group-hover:pr-2 select-none">
        <Button
          variant="outline"
          size="icon"
          className="size-7 opacity-80 bg-white hover:bg-white"
        >
          <VolumeUp className="size-4" />
        </Button>
        <motion.div
          initial={{ opacity: 0, filter: "blur(4px)" }}
          animate={{
            opacity: isHovered ? 1 : 0,
            filter: isHovered ? "blur(0px)" : "blur(4px)",
          }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          className="absolute left-8 top-1/2 -translate-y-1/2 pointer-events-none text-xs whitespace-nowrap overflow-hidden pl-1 text-muted-foreground"
        >
          <ArrowLeft className="size-2" />
        </motion.div>
        <motion.span
          initial={{ opacity: 0, width: 0, filter: "blur(4px)" }}
          animate={{
            opacity: isHovered ? 1 : 0,
            width: isHovered ? "auto" : 0,
            filter: isHovered ? "blur(0px)" : "blur(4px)",
          }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          className="absolute left-12 top-1/2 -translate-y-1/2 pointer-events-none text-xs whitespace-nowrap overflow-hidden text-muted-foreground"
        >
          Click to test your audio
        </motion.span>
      </ControlFieldset>
    </motion.div>
  );
};

const isDev = process.env.NODE_ENV === "development";
export const DevDeviceControlPanel = () => {
  if (!isDev) return null;
  return <UserMediaDeviceDebugPanel />;
};

export const UserMedia = Object.assign(UserMediaRoot, {
  Player: UserMediaPlayer,
  Overlay: UserMediaOverlay,
  OverlayFooter: UserMediaOverlayFooter,
  Controls: UserMediaControls,
  AudioControl: UserMediaAudioControl,
  VideoControl: UserMediaVideoControl,
  SpeakerControl: UserMediaSpeakerControl,
  DevDeviceControlPanel,
});
